import { faChevronLeft as chevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { useEffect, useState, useRef } from "react";
import styles from "../../../styles/SubscribeThematics.module.scss";

import { DISEASE_LS_ID } from "../constants";
import useLocalStorage from "../../../hooks/useLocalStorage";
import CartSection from "./Cart";
import NuveeInfoHint from "../../UI/NuveeInfoHint";
import DiseaseCard from "./DiseaseCard";

const MAX_DISEASE = 2;

/**
 * Root component of the `/subscribe/liberal` route, @ step 1 "Thématiques"
 *
 * @param {Function} setSelectedThematics - Get the selected thematics
 */
export default function SubscribeThematics({ nextStep = () => {} }) {
  const apIUrl = (path = "") => `${process.env.REACT_APP_NUVEE_BASE_URL ?? ""}${path}`;
  const showcaseUrl = process.env.SHOWCASE_URL ?? "https://nuvee.fr";
  const [diseases, setDiseases] = useState();
  const [selectedDiseases, setSelectedDiseases] = useState([]);
  const [localStorageDiseases, setLocalStorageDiseases] = useLocalStorage(DISEASE_LS_ID, []);
  const openModalRef = useRef(null);

  // On component mount, fetch the thematics
  useEffect(() => {
    let isMounted = true;

    axios
      .get(apIUrl("/thematic-diseases/offers"))
      .then(({ data }) => !diseases && isMounted && setDiseases(data ?? []))
      .catch(console.error);

    // If there are thematics in the local storage, set them as selected
    if (localStorageDiseases.length) setSelectedDiseases(localStorageDiseases.map((t) => t.id));

    return () => {
      isMounted = false;
    };
  }, []);

  const getDiseaseFromId = (id) =>
    (diseases ?? [])
      .flat()
      .find((t) => t.id === id);

  // Save the selected thematics in the local storage
  useEffect(() => diseases && setLocalStorageDiseases(selectedDiseases.map(getDiseaseFromId).filter(Boolean)), [selectedDiseases]);

  /** Toggle the selected thematic.
   *
   * If MAX_THEMATICS is reached, replace the last choice with the new one.
   */
  const toggleDisease = (id, isSelected) => {
    if (isSelected && selectedDiseases.length >= MAX_DISEASE && !selectedDiseases.includes(id)) {
      openModalRef.current?.();
      return false;
    }

    setSelectedDiseases((prev) => {
      if (!isSelected && prev.includes(id)) {
        return prev.filter((v) => v !== id);
      } else if (isSelected && !prev.includes(id)) {
        return [...prev, id];
      }

      return prev;
    });
  };

  return (
    <div className={styles.SubscribeThematics}>
      <div className={styles.thematics}>
        <a href={`${showcaseUrl}/pro-de-sante`} className={styles.backButton}>
          <FontAwesomeIcon icon={chevronLeft} />
          Retour
        </a>
        <h1>Sélectionnez vos thématiques * :</h1>
        {diseases ? (
          <div className={styles.diseasesContainer}>
            {[...diseases]
              .sort((a, b) => a.orderDisplay - b.orderDisplay)
              .map((disease, i) => (
                <DiseaseCard 
                  key={`disease-${disease.name?.trim()?.toLowerCase()?.replaceAll(" ", "-")}`}
                  disease={disease}
                  selected={selectedDiseases.includes(disease.id)}
                  toggleDisease={toggleDisease}
                />
              ))}
          </div>
        ) : (
          <div className={styles.loader}>
            <CircularProgress />
          </div>
        )}
      </div>
      <CartSection selectedThematics={selectedDiseases} goToCart={nextStep} />
      <NuveeInfoHint
        infoTitle={"liberal_max_diseases"}
        type={"modal"}
        iconSize="100%"
        sx={{ position: "absolute", width: "0px", height: "0px", zIndex: 10000 }}
        customTrigger={({ openModal }) => {
          if (openModalRef) openModalRef.current = openModal;
          return <></>;
        }}
      />
    </div>
  );
}
